import { useEffect, useRef, useState } from 'react';
import { BlockProps } from '@/components/Block/Block';
import P from '@/components/Redactie/P/P';
import BlockEditorContainer from '@/components/Block/BlockEditorContainer';
import { UserMessageBlockViewerContainer } from '@/components/Block/BlockViewerContainer';
import {
    mdiArrowRightCircle,
    mdiDeleteCircle,
    mdiPauseCircle,
    mdiPlayCircle,
    mdiTimer,
} from '@mdi/js';
import { useBlockLocalUserData } from '@/hooks/useBlockLocalUserData';
import TextButton from '@/components/Button/TextButton';

import styles from './CountdownBlock.module.sass';
import { Checkbox, FormControlLabel } from '@mui/material';

interface CountdownBlockData {
    title: string;
    seconds: number;
    is_instant_transition_enabled: boolean;
}
interface CountdownBlockLocalData {
    seconds_left: number;
}

const CountdownBlock = ({
    subjectId,
    id,
    data,
    uuid,
    updatedAt,
    isEditable,
    isEditorPreview,
    isAnimated,
    onFinished,
    onEdited,
    onContentHeightChanged,
}: BlockProps) => {
    const [blockData, setBlockData] = useState<CountdownBlockData>({
        title: data.title ?? 'Timer',
        seconds: data.seconds ?? 60,
        is_instant_transition_enabled: !!data.is_instant_transition_enabled,
    });

    if (isEditable)
        return (
            <CountdownBlockEditor
                uuid={uuid}
                blockData={blockData}
                setBlockData={setBlockData}
                onEdited={onEdited}
            />
        );
    else
        return (
            <CountdownBlockViewer
                subjectId={subjectId}
                id={id}
                blockData={blockData}
                updatedAt={updatedAt}
                isEditorPreview={isEditorPreview}
                isAnimated={isAnimated}
                onFinished={onFinished}
                onContentHeightChanged={onContentHeightChanged}
            />
        );
};

const MIN_TIMER_DURATION = 5;
const MAX_TIMER_DURATION = 60 * 15;

const CountdownBlockEditor = ({
    uuid,
    blockData,
    setBlockData,
    onEdited,
}: {
    uuid: BlockProps['uuid'];
    blockData: CountdownBlockData;
    setBlockData: (data: CountdownBlockData) => void;
    onEdited: BlockProps['onEdited'];
}) => {
    const [localSecondsValue, setLocalSecondsValue] = useState(
        blockData.seconds.toString(),
    );

    useEffect(() => {
        const seconds = parseInt(localSecondsValue);
        if (
            !isNaN(seconds) &&
            seconds >= MIN_TIMER_DURATION &&
            seconds <= MAX_TIMER_DURATION
        )
            setBlockData({ ...blockData, seconds });
    }, [localSecondsValue]);

    useEffect(() => {
        onEdited?.({
            uuid,
            data: JSON.parse(JSON.stringify(blockData)),
            attachments: {},
        });
    }, [blockData]);

    return (
        <BlockEditorContainer className="grid-cols-2 bg-teal-200">
            <P
                isEditable
                value={blockData.title}
                onChange={(value) => {
                    setBlockData({ ...blockData, title: value });
                }}
                placeholder="Titel"
                rows={1}
            />
            <P
                isEditable
                value={localSecondsValue}
                onChange={(value) =>
                    setLocalSecondsValue(value.replace(/\D/g, ''))
                }
                placeholder="Aantal seconden"
                rows={1}
            />
            <div className="col-span-2 pl-2 bg-white rounded-md border-2">
                <FormControlLabel
                    value="end"
                    onChange={(event) => {
                        const checked = (event.target as HTMLInputElement)
                            .checked;
                        setBlockData({
                            ...blockData,
                            is_instant_transition_enabled: checked,
                        });
                    }}
                    control={
                        <Checkbox
                            size="small"
                            checked={blockData.is_instant_transition_enabled}
                        />
                    }
                    label={
                        <span className="text-sm font-bold">
                            Niet wachten met chat tot timer is afgerond
                        </span>
                    }
                    labelPlacement="end"
                />
            </div>
        </BlockEditorContainer>
    );
};

const CountdownBlockViewer = ({
    subjectId,
    id,
    blockData,
    updatedAt,
    isEditorPreview,
    isAnimated,
    onFinished,
    onContentHeightChanged,
}: {
    subjectId: BlockProps['subjectId'];
    id: BlockProps['id'];
    blockData: CountdownBlockData;
    updatedAt: BlockProps['updatedAt'];
    isEditorPreview: BlockProps['isEditorPreview'];
    isAnimated: BlockProps['isAnimated'];
    onFinished: BlockProps['onFinished'];
    onContentHeightChanged: BlockProps['onContentHeightChanged'];
}) => {
    const selfRef = useRef<HTMLDivElement>(null);

    const {
        userDataLocal,
        setUserDataLocal,
    } = useBlockLocalUserData<CountdownBlockLocalData>(
        subjectId,
        id,
        updatedAt,
        isEditorPreview,
        {
            seconds_left: blockData.seconds,
        },
    );

    const [isRunning, setIsRunning] = useState(false);
    const [hasFinished, setHasFinished] = useState(
        userDataLocal.seconds_left <= 0,
    );

    const hasStarted = userDataLocal.seconds_left < blockData.seconds;

    useEffect(() => {
        if (userDataLocal.seconds_left <= 0) {
            setHasFinished(true);
            onFinished?.();
        }
        if (blockData.is_instant_transition_enabled) {
            setTimeout(() => {
                if (selfRef.current) {
                    onFinished?.();
                }
            }, 1500);
        }
    }, [userDataLocal.seconds_left, blockData.is_instant_transition_enabled]);

    useEffect(() => {
        onContentHeightChanged?.();
    }, [hasStarted, isRunning]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (
                isRunning &&
                selfRef.current &&
                userDataLocal.seconds_left > 0
            ) {
                setUserDataLocal({
                    ...userDataLocal,
                    seconds_left: userDataLocal.seconds_left - 1,
                });

                // After the second second, trigger onContentHeightChanged once
                if (userDataLocal.seconds_left === blockData.seconds - 2)
                    onContentHeightChanged?.();
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [isRunning, selfRef.current, userDataLocal.seconds_left]);

    let displayTimeLeft = userDataLocal.seconds_left.toString();
    if (userDataLocal.seconds_left > 60) {
        const minutes = Math.floor(userDataLocal.seconds_left / 60);
        const seconds = userDataLocal.seconds_left % 60;
        displayTimeLeft = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    }

    const isSmall = blockData.is_instant_transition_enabled;

    return (
        <UserMessageBlockViewerContainer
            label={blockData.title}
            icon={mdiTimer}
            iconSize={0.75}
            isCollapsed={hasFinished && !isRunning}
            isAnimated={isAnimated}
        >
            <div
                className={`${styles['countdownblock']} ${
                    isSmall ? styles['countdownblock--small'] : ''
                }`}
            >
                <div
                    ref={selfRef}
                    className={styles['countdownblock__timercontainer']}
                >
                    <CountdownIllustration
                        percentageCompleted={
                            (userDataLocal.seconds_left / blockData.seconds) *
                            100
                        }
                    />
                    <p
                        aria-hidden
                        className={`${
                            styles['countdownblock__timercontainer__timer']
                        } ${
                            displayTimeLeft.length > 2
                                ? styles[
                                      'countdownblock__timercontainer__timer--moretext'
                                  ]
                                : ''
                        }`}
                    >
                        {displayTimeLeft}
                    </p>
                </div>
                <div className="sr-only" aria-live="polite">
                    {Math.round(userDataLocal.seconds_left / 10) * 10} seconden
                    over
                </div>
                <div className={styles['countdownblock__buttoncontainer']}>
                    {userDataLocal.seconds_left > 0 && (
                        <TextButton
                            onClick={() => {
                                if (!isRunning && !hasStarted)
                                    setUserDataLocal({
                                        ...userDataLocal,
                                        seconds_left: blockData.seconds,
                                    });
                                setIsRunning(!isRunning);
                            }}
                            icon={isRunning ? mdiPauseCircle : mdiPlayCircle}
                            isSmall={isSmall || hasStarted}
                            isPrimary={!isRunning}
                        >
                            {isRunning
                                ? 'Pauzeren'
                                : !hasStarted
                                ? 'Starten'
                                : 'Doorgaan'}
                        </TextButton>
                    )}
                    {hasStarted && (
                        <>
                            <TextButton
                                onClick={() => {
                                    setIsRunning(false);
                                    setUserDataLocal({
                                        ...userDataLocal,
                                        seconds_left: blockData.seconds,
                                    });
                                }}
                                icon={mdiDeleteCircle}
                                isSmall
                                isPrimary={false}
                            >
                                Reset
                            </TextButton>

                            {!hasFinished && (
                                <TextButton
                                    onClick={() => {
                                        onFinished?.();
                                        setIsRunning(false);
                                        setHasFinished(true);
                                    }}
                                    icon={mdiArrowRightCircle}
                                    isSmall
                                    isPrimary={false}
                                >
                                    Afronden
                                </TextButton>
                            )}
                        </>
                    )}
                </div>
            </div>
        </UserMessageBlockViewerContainer>
    );
};

const CountdownIllustration = ({
    percentageCompleted,
}: {
    percentageCompleted: number;
}) => (
    <svg
        aria-hidden
        xmlns="http://www.w3.org/2000/svg"
        className={styles['countdownblock__timercontainer__illustration']}
        fill="none"
        viewBox="0 0 512 512"
        style={{
            transform: `rotate(${
                (percentageCompleted / 100) * 360
            }deg) scale(0.96)`,
        }}
    >
        <path
            fill="url(#paint0_linear_1616_161)"
            fillRule="evenodd"
            d="M256 10c135.862 0 246 110.138 246 246S391.862 502 256 502v-28c120.398 0 218-97.602 218-218S376.398 38 256 38V10z"
            clipRule="evenodd"
        ></path>
        <circle
            cx="25"
            cy="25"
            r="22"
            fill="var(--illustration-color)"
            stroke="#fff"
            strokeWidth="6"
            transform="matrix(-1 0 0 1 281 0)"
        ></circle>
        <defs>
            <linearGradient
                id="paint0_linear_1616_161"
                x1="275.5"
                x2="236.5"
                y1="15"
                y2="476"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor="var(--illustration-color)"></stop>
                <stop
                    offset="0.031"
                    stopColor="var(--illustration-color)"
                ></stop>
                <stop
                    offset="1"
                    stopColor="var(--illustration-color)"
                    stopOpacity="0"
                ></stop>
            </linearGradient>
        </defs>
    </svg>
);

export default CountdownBlock;
